import { useEffect, useState } from 'react';
import { Popconfirm } from 'antd';

import {
  createExpensesInParts,
  createExpenseHead,
  expensesKey,
  cashKey,
  deleteExpenseHead,
  saveRecurringExpense,
  skipRecurringExpense,
  undoRecurringSkip,
  updateExpenseHead,
  useDueExpenses,
  useExpenseAccounts,
  useExpenseHeads,
  useExpenseSummary,
  useExpenses,
  useRecurringExpenses,
  voidExpense,
  type CreateExpenseInput,
  type DueExpense,
  type Expense,
  type ExpenseHead,
  type SaveRecurringExpenseInput,
} from '@/api/cash';
import { useApiMutation } from '@/api/mutations';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { occurrenceWords } from '@core/cash/recurring';
import { againLikeLastTime } from '@core/cash/expense';
import type { SplitPart } from '@core/cash/split';
import { accountHue } from '@core/cash/board';
import {
  accountShares,
  biggestHead,
  headBars,
  monthOf,
  spendingPeriodLabels,
  spendingRange,
  spendingSentence,
  type SpendingPeriod,
} from '@core/cash/spending';
import { hueClasses } from '@/components/hues';
import { useDebounced } from '@/components/useDebounced';
import { formatAmount } from '@core/format/money';
import type { PaymentMethod } from '@core/generated/enums';
import { formatDate, toDayjs, toIsoDate } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { NewExpensePage } from './NewExpensePage';
import { DueCard, RecurringCard } from './RecurringCards';
import { RecurringExpensePage } from './RecurringExpensePage';

/**
 * Section 18's spending register, and the heads it is filed under.
 *
 * <p>
 * Two things on one screen because they are one idea — the vocabulary is only meaningful through
 * what is booked against it, which is the argument the service behind them already makes.
 * </p>
 *
 * <p>
 * <strong>There is no edit action on a voucher, only void.</strong> Not an omission: a voucher says
 * money left the drawer on a day, and editing one rewrites what the cash book already reported. The
 * number stays in the sequence and the ledger takes a reversing line.
 * </p>
 */
export function ExpensesPage({
  session,
  startNew = false,
  likeHeadId,
  dueId,
  onOpen,
}: {
  session: Session;
  /** Opens on Record an expense — Money out on the cash board. */
  startNew?: boolean;
  /** Opens on a voucher filled like the last one under this head ("Again, like last time"). */
  likeHeadId?: number | undefined;
  /** Opens on the voucher for this recurring expense's due occurrence. */
  dueId?: number | undefined;
  /** Opens another screen — a voucher started on Cash & bank goes back there when it is done. */
  onOpen?: ((key: string, view?: string) => void) | undefined;
}) {
  const branchId = session.branch?.id;
  const fromBoard = startNew || likeHeadId !== undefined || dueId !== undefined;

  const [screen, setScreen] = useState<'register' | 'new'>(fromBoard ? 'new' : 'register');
  // Bumped by Save and record another, so the page comes back empty.
  const [round, setRound] = useState(0);
  const [showHeads, setShowHeads] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [period, setPeriod] = useState<SpendingPeriod>('month');
  const [headFilter, setHeadFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Recording a due recurring expense — the voucher opens filled from it (step 8).
  const [recording, setRecording] = useState<DueExpense | undefined>(undefined);
  // The recurring-expense editor: a template's id, or 'new'.
  const [editingRecurring, setEditingRecurring] = useState<number | 'new' | null>(null);
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const month = monthOf(today);
  const range = spendingRange(period, today);
  const typed = useDebounced(search.trim(), 300);

  const heads = useExpenseHeads(branchId, true);
  const accounts = useExpenseAccounts(
    screen === 'new' || editingRecurring !== null ? branchId : undefined,
  );
  const expenses = useExpenses(branchId, {
    includeVoided,
    page,
    from: range.from,
    to: range.to,
    headId: headFilter,
    search: typed,
  });
  // The register's newest page, unfiltered — what "Again, like last time" reads.
  const recent = useExpenses(likeHeadId !== undefined ? branchId : undefined, { pageSize: 25 });
  const due = useDueExpenses(branchId);
  const thisMonth = useExpenseSummary(branchId, month.from, month.to);
  const todaySpent = useExpenseSummary(branchId, today, today);
  const recurring = useRecurringExpenses(
    showRecurring || editingRecurring !== null ? branchId : undefined,
    true,
  );

  const mayWrite = canAct(session, 'Expense:Create');

  // A sub-page opens at its top. Without this, Record from a long Due list opened the voucher scrolled
  // down, its title and Save under the top bar (2026-10-23).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen, editingRecurring, round]);

  // A due item asked for by id (Money out's tile) opens filled from it once the list is in.
  const dueAsked =
    dueId === undefined
      ? undefined
      : due.data?.find((item) => item.recurringExpenseId === dueId && item.isNext);

  const leave = () => {
    setRecording(undefined);

    if (fromBoard && onOpen) {
      onOpen('cash');
    } else {
      setScreen('register');
    }
  };

  // One voucher per account (split payments step 6); a refusal part-way keeps the page open.
  const create = useApiMutation({
    mutationFn: ({
      requests,
      parts,
    }: {
      requests: CreateExpenseInput[];
      parts: readonly SplitPart[];
      again: boolean;
    }) => createExpensesInParts(requests, parts),
    // Names the voucher and where the money left, so the clerk knows which one went down.
    success: ({ saved, failed }, { requests }) => {
      const first = saved[0]?.result;
      const input = requests[0];

      return failed
        ? `Part saved — ${saved.length} of ${saved.length + 1} vouchers went down.`
        : saved.length > 1
          ? `${saved.length} vouchers · ₹${formatAmount(saved.reduce((sum, each) => sum + each.result.amount, 0))}.`
          : !first
            ? 'Saved.'
            : input?.recurringExpenseId && input.recurrenceDueOn
              ? `${first.documentNumber} · ${due.data?.find((item) => item.recurringExpenseId === input.recurringExpenseId)?.name ?? first.headName}, due ${formatDate(input.recurrenceDueOn)}, recorded.`
              : `${first.documentNumber} · ₹${formatAmount(first.amount)}${first.cashAccountName ? ` from ${first.cashAccountName}` : ''}.`;
    },
    // Both keys: the voucher lands on this register *and* changes what is in the drawer, so a cash
    // screen left holding the previous figure would disagree with the row a clerk just saw appear.
    invalidates: [expensesKey, cashKey],
    onSuccess: ({ failed }, { again }) => {
      if (failed) {
        return;
      }

      setRecording(undefined);

      if (again) {
        setRound((n) => n + 1);
      } else {
        leave();
      }
    },
  });

  const saveRecurring = useApiMutation({
    mutationFn: (input: SaveRecurringExpenseInput) =>
      saveRecurringExpense(editingRecurring === 'new' ? null : editingRecurring, input),
    success: (saved) =>
      saved.nextDueOn
        ? `${saved.name} saved — next due ${formatDate(saved.nextDueOn)}.`
        : `${saved.name} saved.`,
    invalidates: [expensesKey],
    onSuccess: () => setEditingRecurring(null),
  });

  const skip = useApiMutation({
    mutationFn: (item: DueExpense) =>
      skipRecurringExpense(item.recurringExpenseId, item.dueOn.slice(0, 10)),
    success: (saved) =>
      saved.nextDueOn
        ? `Skipped — ${saved.name} is next due ${formatDate(saved.nextDueOn)}.`
        : 'Skipped.',
    invalidates: [expensesKey],
  });

  const undoSkip = useApiMutation({
    mutationFn: ({ id, skipId }: { id: number; skipId: number }) => undoRecurringSkip(id, skipId),
    success: (saved) =>
      saved.nextDueOn
        ? `Skip undone — ${saved.name} is due ${formatDate(saved.nextDueOn)} again.`
        : 'Skip undone.',
    invalidates: [expensesKey],
  });

  const discard = useApiMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => voidExpense(id, reason),
    success: 'Voucher voided.',
    invalidates: [expensesKey, cashKey],
  });

  if (editingRecurring !== null) {
    return (
      <RecurringExpensePage
        template={
          editingRecurring === 'new'
            ? undefined
            : recurring.data?.find((template) => template.id === editingRecurring)
        }
        heads={heads.data ?? []}
        accounts={accounts.data}
        busy={saveRecurring.isPending || undoSkip.isPending}
        mayAddHead={mayWrite}
        onCancel={() => setEditingRecurring(null)}
        onSave={(input) => saveRecurring.mutate(input)}
        onUndoSkip={
          mayWrite && typeof editingRecurring === 'number'
            ? (skipId) => undoSkip.mutate({ id: editingRecurring, skipId })
            : undefined
        }
      />
    );
  }

  if (screen === 'new') {
    // A voucher filled from something still loading waits for it: its first values are read once.
    const waiting =
      round === 0 &&
      ((likeHeadId !== undefined && (recent.isLoading || heads.isLoading)) ||
        (dueId !== undefined && due.isLoading));

    if (waiting) {
      return (
        <Card>
          <p className="text-default mb-0 text-[length:var(--scale-list-name)]">
            Opening the voucher…
          </p>
        </Card>
      );
    }

    const like =
      round === 0 && likeHeadId !== undefined
        ? againLikeLastTime(recent.data?.items ?? [], heads.data, 25).find(
            (tile) => tile.headId === likeHeadId,
          )
        : undefined;

    return (
      <NewExpensePage
        key={round}
        heads={heads.data ?? []}
        accounts={accounts.data}
        from={recording ?? (round === 0 ? dueAsked : undefined)}
        start={
          like
            ? { headId: like.headId, method: like.method, cashAccountId: like.cashAccountId }
            : undefined
        }
        usage={thisMonth.data}
        branchName={session.branch?.name}
        busy={create.isPending}
        mayAddHead={mayWrite}
        onCancel={leave}
        onSave={(requests, parts, again) => create.mutateAsync({ requests, parts, again })}
      />
    );
  }

  const biggest = biggestHead(thisMonth.data);
  const dueTotal = (due.data ?? []).reduce((sum, item) => sum + item.typicalAmount, 0);
  const bars = headBars(thisMonth.data);
  const shares = accountShares(thisMonth.data);
  const filterHeads = (thisMonth.data?.byHead ?? []).slice(0, 4);

  const figures = [
    {
      key: 'month',
      label: 'This month',
      figure: thisMonth.data ? `₹${formatAmount(thisMonth.data.total)}` : '…',
      hint: thisMonth.data
        ? `${thisMonth.data.count} ${thisMonth.data.count === 1 ? 'voucher' : 'vouchers'} · ${month.name}`
        : month.name,
      icon: 'icon icon-indian-rupee',
      hue: 'primary' as const,
      attention: false,
      words: false,
    },
    {
      key: 'today',
      label: 'Today',
      figure: todaySpent.data ? `₹${formatAmount(todaySpent.data.total)}` : '…',
      hint: todaySpent.data
        ? `${todaySpent.data.count} ${todaySpent.data.count === 1 ? 'voucher' : 'vouchers'}`
        : '',
      icon: 'icon icon-arrow-up-right',
      hue: 'teal' as const,
      attention: false,
      words: false,
    },
    {
      key: 'due',
      label: 'Due this week',
      figure: `₹${formatAmount(dueTotal)}`,
      hint:
        (due.data?.length ?? 0) === 0
          ? 'Nothing recurring is due'
          : `${due.data?.length} recurring · ${due.data?.[0]?.name ?? ''} first`,
      icon: 'icon icon-repeat',
      hue: 'warning' as const,
      attention: (due.data?.length ?? 0) > 0,
      words: false,
    },
    {
      key: 'biggest',
      label: 'Biggest head',
      figure: biggest?.name ?? '—',
      hint: biggest
        ? `₹${formatAmount(biggest.amount)} · ${biggest.share}% of ${month.name}`
        : 'Nothing spent yet',
      icon: 'icon icon-chart-bar',
      hue: 'indigo' as const,
      attention: false,
      // A name, not a figure: smaller, so a long head still fits the card.
      words: true,
    },
  ];

  const columns: DataViewColumn<Expense>[] = [
    {
      title: 'Paid on',
      dataIndex: 'paidOn',
      hideOnCard: true,
      render: (value: string) => (
        <span
          className="bg-light flex size-11 flex-col items-center justify-center rounded-[10px] leading-none text-gray-900"
          aria-label={formatDate(value)}
        >
          <span className="text-[15px] font-bold">{toDayjs(value)?.format('DD')}</span>
          <span className="mt-0.5 text-[10px] font-semibold tracking-[0.06em] uppercase">
            {toDayjs(value)?.format('MMM')}
          </span>
        </span>
      ),
    },
    {
      title: 'Head',
      dataIndex: 'headName',
      primary: true,
      render: (value: string, row: Expense) => (
        <span className="flex min-w-0 flex-col">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
              {value}
            </span>
            {row.isVoided && <StatusBadge tone="negative">Voided</StatusBadge>}
            {occurrenceWords(row) && (
              <StatusBadge tone="neutral">{occurrenceWords(row)}</StatusBadge>
            )}
          </span>
          <span className="text-default text-[length:var(--scale-list-meta)]">
            {[row.documentNumber, row.notes ?? row.reference].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      title: 'How',
      dataIndex: 'method',
      render: (value: string, row: Expense) => (
        <span className="flex flex-col">
          <span className="text-default">
            {paymentMethodLabel[value as PaymentMethod] ?? humanizeEnum(value)}
          </span>
          {row.cashAccountName && (
            <span className="text-[length:var(--scale-list-meta)] text-gray-900">
              from {row.cashAccountName}
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      render: (value: number, row: Expense) => (
        <span
          className={`tabular-nums ${row.isVoided ? 'text-default line-through' : 'font-semibold text-gray-900'}`}
        >
          ₹{formatAmount(value)}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      trailing: true,
      render: (_: unknown, row: Expense) =>
        row.isVoided ? (
          <span className="text-default text-[12px]">{row.voidReason}</span>
        ) : (
          <WriteAction session={session} required="Expense:Edit">
            {(disabled) => (
              <VoidAction
                busy={discard.isPending || disabled}
                onVoid={(reason) => discard.mutate({ id: row.id, reason })}
              />
            )}
          </WriteAction>
        ),
    },
  ];

  return (
    <>
      <PageHeading
        eyebrow={['Money', session.branch?.name].filter(Boolean).join(' · ')}
        title="Expenses"
        subtitle={
          spendingSentence(thisMonth.data, month.name, due.data ?? [], today, '₹') ||
          'What the shop spent, and the head it was filed under.'
        }
        overlap
        actions={
          <>
            <Button onClick={() => setShowHeads((open) => !open)}>
              {showHeads ? 'Hide heads' : 'Heads'}
            </Button>
            <Button icon="icon icon-repeat" onClick={() => setShowRecurring((open) => !open)}>
              {showRecurring ? 'Hide recurring' : 'Recurring'}
            </Button>
            {mayWrite && (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                onClick={() => setScreen('new')}
              >
                Record an expense
              </Button>
            )}
          </>
        }
      />

      <section
        aria-label="Spending in four figures"
        className="mb-4 grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {figures.map((figure) => (
          <div
            key={figure.key}
            className="ar-card flex items-center justify-between gap-3 p-4 lg:p-5"
          >
            <span className="min-w-0">
              <span className="text-default mb-1 block truncate text-[length:var(--scale-list-name)]">
                {figure.label}
              </span>
              <span
                className={`block truncate font-bold tabular-nums ${figure.words ? 'text-[22px] leading-[34px]' : 'text-[28px] leading-tight'} ${
                  figure.attention ? 'text-warning' : 'text-gray-900'
                }`}
              >
                {figure.figure}
              </span>
              <span className="text-default block truncate text-[length:var(--scale-list-meta)]">
                {figure.hint}
              </span>
            </span>
            <span
              className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${hueClasses[figure.hue].tile}`}
              aria-hidden="true"
            >
              <i className={`${figure.icon} text-lg text-[#ffffff]`} />
            </span>
          </div>
        ))}
      </section>

      <div className="flex flex-col gap-4">
        {showHeads && <HeadsCard session={session} heads={heads.data ?? []} />}

        {showRecurring && (
          <RecurringCard
            session={session}
            templates={recurring.data ?? []}
            loading={recurring.isLoading}
            onEdit={(id) => setEditingRecurring(id)}
            onNew={() => setEditingRecurring('new')}
          />
        )}

        <div className="grid grid-cols-12 items-start gap-4">
          <Card flush className="col-span-12 overflow-hidden pb-2 xl:col-span-8">
            <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
              <div>
                <h2 className="mb-0 text-[17px] font-bold text-gray-900">Vouchers</h2>
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  What the shop spent, and the head it was filed under.
                </p>
              </div>
              <div
                role="group"
                aria-label="Period"
                className="bg-light flex gap-1 rounded-lg p-[3px]"
              >
                {(Object.keys(spendingPeriodLabels) as SpendingPeriod[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={period === option}
                    onClick={() => {
                      setPeriod(option);
                      setPage(1);
                    }}
                    className={`h-[30px] cursor-pointer rounded-md px-3 text-[13px] text-gray-900 ${
                      period === option ? 'bg-white font-semibold shadow-sm' : ''
                    }`}
                  >
                    {spendingPeriodLabels[option]}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-border-color flex flex-wrap items-center gap-2.5 border-b px-5 py-3">
              <label className="border-border-color flex h-[38px] min-w-0 flex-[1_1_240px] items-center gap-2 rounded-lg border bg-white px-3">
                <i className="icon icon-search text-default" aria-hidden="true" />
                <span className="sr-only">Search vouchers</span>
                <input
                  // No type: the template draws its own border on every typed input, inside this box.
                  enterKeyHint="search"
                  placeholder="Number, head, notes, reference"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-gray-900 outline-none"
                />
              </label>
              {filterHeads.length > 0 && (
                <div role="group" aria-label="Head" className="flex flex-wrap gap-1.5">
                  {[{ expenseHeadId: undefined, headName: 'All heads' }, ...filterHeads].map(
                    (option) => (
                      <button
                        key={option.expenseHeadId ?? 'all'}
                        type="button"
                        aria-pressed={headFilter === option.expenseHeadId}
                        onClick={() => {
                          setHeadFilter(option.expenseHeadId);
                          setPage(1);
                        }}
                        className={`h-[30px] cursor-pointer rounded-full border px-3 text-[12px] ${
                          headFilter === option.expenseHeadId
                            ? 'border-primary bg-primary font-semibold text-white'
                            : 'border-border-color hover:bg-light bg-white text-gray-900'
                        }`}
                      >
                        {option.headName}
                      </button>
                    ),
                  )}
                </div>
              )}
              <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-900">
                <input
                  type="checkbox"
                  checked={includeVoided}
                  onChange={(event) => {
                    setIncludeVoided(event.target.checked);
                    setPage(1);
                  }}
                />
                Show voided
              </label>
            </div>

            <div className="px-2">
              <DataView
                columns={columns}
                dataSource={expenses.data?.items ?? []}
                loading={expenses.isLoading}
                empty={
                  <EmptyState
                    icon="icon icon-receipt"
                    title={
                      typed || headFilter !== undefined ? 'No voucher matches' : 'Nothing spent'
                    }
                    hint="Rent, electricity, the karigar's chai — every voucher recorded here comes straight off the drawer."
                  />
                }
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-3 pb-1">
              <span className="text-default text-[length:var(--scale-list-meta)]">
                {(expenses.data?.total ?? 0) === 1
                  ? '1 voucher'
                  : `${expenses.data?.total ?? 0} vouchers`}
                {includeVoided ? '' : ' · voided ones are left out'}
              </span>
              {(expenses.data?.total ?? 0) > 25 && (
                <span className="flex gap-1.5">
                  <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button
                    disabled={page * 25 >= (expenses.data?.total ?? 0)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </span>
              )}
            </div>
          </Card>

          <aside className="col-span-12 flex min-w-0 flex-col gap-4 xl:col-span-4">
            {/* What is due from the recurring expenses (step 8) — shown whenever there is any. */}
            {(due.data?.length ?? 0) > 0 && (
              <DueCard
                session={session}
                due={due.data ?? []}
                today={today}
                busy={skip.isPending}
                onRecord={(item) => {
                  setRecording(item);
                  setScreen('new');
                }}
                onSkip={(item) => skip.mutate(item)}
                onAll={() => setShowRecurring(true)}
              />
            )}

            {bars.length > 0 && (
              <Card>
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="mb-0 text-[15px] font-bold text-gray-900">{month.name} by head</h2>
                  <span className="text-default tabular-nums text-[12px]">
                    ₹{formatAmount(thisMonth.data?.total ?? 0)}
                  </span>
                </div>
                <ul className="mt-3 flex flex-col gap-2.5 ps-0">
                  {bars.map((bar) => (
                    <li key={bar.key} className="list-none">
                      <div className="flex justify-between text-[12px]">
                        <span className="text-gray-900">{bar.label}</span>
                        <span className="tabular-nums font-semibold text-gray-900">
                          ₹{formatAmount(bar.amount)}
                        </span>
                      </div>
                      <div className="bg-light mt-1 h-1.5 overflow-hidden rounded-full">
                        <div
                          className="bg-primary h-1.5 rounded-full"
                          style={{ width: `${bar.width}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>

                {shares.length > 0 && (
                  <div className="border-border-color mt-3.5 border-t pt-3">
                    <p className="text-default mb-2 text-[12px]">Paid from</p>
                    <div
                      className="flex h-2.5 gap-0.5 overflow-hidden rounded-full"
                      aria-hidden="true"
                    >
                      {shares.map((share) => (
                        <div
                          key={share.cashAccountId}
                          className={hueClasses[accountHue[share.kind]].tile}
                          style={{ width: `${share.width}%` }}
                        />
                      ))}
                    </div>
                    <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 ps-0 text-[12px] text-gray-900">
                      {shares.map((share) => (
                        <li
                          key={share.cashAccountId}
                          className="flex list-none items-center gap-1.5"
                        >
                          <span
                            className={`size-2 rounded-full ${hueClasses[accountHue[share.kind]].tile}`}
                            aria-hidden="true"
                          />
                          {share.accountName} ₹{formatAmount(share.amount)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}
          </aside>
        </div>
      </div>
    </>
  );
}

/**
 * Voiding asks for a reason before it fires — compulsory on the server, so asking here mirrors it
 * rather than letting a clerk reach a 400.
 *
 * <p>
 * <strong>A `Popconfirm`, and the first attempt was an inline input in the cell — which was wrong
 * in a way only driving it showed.</strong> `style.css` puts `text-nowrap` on every `td`, so a cell
 * that grows cannot reflow: the row pushed the table wider than its card and produced a horizontal
 * scrollbar, which is the strip defect `DataView` exists to prevent, arriving from the action
 * column instead of from too many columns. Anchored to the button it opened from, the reason costs
 * the row no width at all.
 * </p>
 */
function VoidAction({ busy, onVoid }: { busy: boolean; onVoid: (reason: string) => void }) {
  const [reason, setReason] = useState('');

  return (
    <Popconfirm
      title="Void this voucher?"
      description={
        <div className="w-56 pt-1">
          <p className="text-default mb-1.5 text-[12px]">
            The number stays in the sequence and the ledger takes a reversing line.
          </p>
          <input
            type="text"
            placeholder="Why?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
          />
        </div>
      }
      okText="Void"
      okButtonProps={{ danger: true, loading: busy, disabled: reason.trim().length === 0 }}
      cancelText="Cancel"
      onConfirm={() => {
        onVoid(reason.trim());
        setReason('');
      }}
      placement="leftTop"
    >
      <Button disabled={busy}>Void</Button>
    </Popconfirm>
  );
}

/** The heads themselves — a small master, kept beside what is booked against it. */
function HeadsCard({ session, heads }: { session: Session; heads: ExpenseHead[] }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const add = useApiMutation({
    mutationFn: (input: { code: string; name: string }) => createExpenseHead(input),
    success: 'Head added.',
    invalidates: [expensesKey],
    onSuccess: () => {
      setCode('');
      setName('');
    },
  });

  const retire = useApiMutation({
    mutationFn: (head: ExpenseHead) =>
      updateExpenseHead(head.id, {
        code: head.code,
        name: head.name,
        isActive: !head.isActive,
      }),
    success: 'Head updated.',
    invalidates: [expensesKey],
  });

  const remove = useApiMutation({
    mutationFn: (id: number) => deleteExpenseHead(id),
    success: 'Head removed.',
    invalidates: [expensesKey],
  });

  return (
    <Card>
      <p className="mb-2.5 text-sm font-semibold text-gray-900">Expense heads</p>

      <ul className="mb-3 flex flex-col gap-1.5 ps-0">
        {heads.map((head) => (
          // Both utilities, and in the two places the template forces: `ps-0` on the `ul` beats the
          // indent, and `list-none` has to be on each `li` because the marker comes from a
          // descendant selector that outranks it on the parent.
          <li
            key={head.id}
            className="border-border-color flex list-none items-center justify-between gap-2 rounded border bg-white px-2.5 py-1.5"
          >
            <span className="flex items-center gap-1.5">
              <span className="text-[13px] font-semibold text-gray-900">{head.name}</span>
              <span className="text-default text-[11px]">{head.code}</span>
              {head.isShared && <StatusBadge tone="brand">All branches</StatusBadge>}
              {!head.isActive && <StatusBadge tone="neutral">Retired</StatusBadge>}
            </span>

            <WriteAction session={session} required="Expense:Edit">
              {(blocked) => (
                <span className="flex gap-1.5">
                  <Button
                    onClick={() => retire.mutate(head)}
                    disabled={blocked || retire.isPending}
                  >
                    {head.isActive ? 'Retire' : 'Bring back'}
                  </Button>
                  <Button
                    onClick={() => remove.mutate(head.id)}
                    disabled={blocked || remove.isPending}
                  >
                    Remove
                  </Button>
                </span>
              )}
            </WriteAction>
          </li>
        ))}
      </ul>

      <WriteAction session={session} required="Expense:Create">
        {(blocked) => (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Code" className="w-28">
              <input
                type="text"
                disabled={blocked}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className={controlClass}
              />
            </Field>
            <Field label="Name" className="min-w-48 flex-1">
              <input
                type="text"
                disabled={blocked}
                value={name}
                onChange={(event) => setName(event.target.value)}
                className={controlClass}
              />
            </Field>
            <Button
              variant="primary"
              disabled={blocked || code.trim() === '' || name.trim() === '' || add.isPending}
              onClick={() => add.mutate({ code: code.trim(), name: name.trim() })}
            >
              Add head
            </Button>
          </div>
        )}
      </WriteAction>

      <Notice tone="info" title="Removing is only for a head nothing was booked against">
        Once spending has been recorded, retire it instead — the vouchers still have to name it.
      </Notice>
    </Card>
  );
}
